import Anthropic from '@anthropic-ai/sdk';
import { UserError } from '../../errors';
import type { Model, ModelMetadata, ModelProvider } from '../../model';
import {
  type DeepSeekAnthropicClient,
  type DeepSeekAnthropicFetch,
  DeepSeekAnthropicMessagesModel,
} from './anthropic-messages-model';
import {
  getDefaultDeepSeekBaseURL,
  getDefaultDeepSeekKey,
  getDefaultDeepSeekModel,
} from './defaults';

export type DeepSeekProviderOptions = {
  apiKey?: string;
  baseURL?: string;
  defaultModel?: string;
  maxRetries?: number;
  fetch?: DeepSeekAnthropicFetch;
  anthropicClient?: DeepSeekAnthropicClient;
  modelMetadata?:
    | Record<string, ModelMetadata>
    | ((modelName: string) => ModelMetadata | undefined);
};

const DEEPSEEK_ANTHROPIC_TIMEOUT_MS = 60 * 60 * 1000;

export class DeepSeekProvider implements ModelProvider {
  #client?: DeepSeekAnthropicClient;
  #modelCache = new Map<string, Model>();
  #options: DeepSeekProviderOptions;

  constructor(options: DeepSeekProviderOptions = {}) {
    if (options.anthropicClient) {
      if (options.apiKey) {
        throw new Error('Cannot provide both apiKey and anthropicClient');
      }
      if (options.baseURL) {
        throw new Error('Cannot provide both baseURL and anthropicClient');
      }
      if (options.fetch) {
        throw new Error('Cannot provide both fetch and anthropicClient');
      }
      this.#client = options.anthropicClient;
    }
    this.#options = options;
  }

  getDefaultModelName(): string {
    return this.#options.defaultModel || getDefaultDeepSeekModel();
  }

  getModelMetadata(modelName?: string): ModelMetadata | undefined {
    const resolvedModelName = modelName || this.getDefaultModelName();
    return (
      resolveConfiguredModelMetadata(
        this.#options.modelMetadata,
        resolvedModelName,
      ) ?? getKnownDeepSeekModelMetadata(resolvedModelName)
    );
  }

  #getClient(): DeepSeekAnthropicClient {
    if (!this.#client) {
      const apiKey = this.#options.apiKey ?? getDefaultDeepSeekKey();
      if (!apiKey) {
        throw new UserError(
          'Missing DeepSeek API key. Pass apiKey to DeepSeekProvider or set DEEPSEEK_API_KEY.',
        );
      }
      this.#client = new Anthropic({
        apiKey,
        baseURL: this.#options.baseURL ?? getDefaultDeepSeekBaseURL(),
        fetch: this.#options.fetch,
        maxRetries: this.#options.maxRetries,
        timeout: DEEPSEEK_ANTHROPIC_TIMEOUT_MS,
      });
    }
    return this.#client;
  }

  async getModel(modelName?: string | undefined): Promise<Model> {
    const model = modelName || this.getDefaultModelName();
    const cacheKey = JSON.stringify([model, 'anthropic_messages']);

    const cachedModel = this.#modelCache.get(cacheKey);
    if (cachedModel) {
      return cachedModel;
    }

    const resolvedModel = new DeepSeekAnthropicMessagesModel({
      baseURL: this.#options.baseURL ?? getDefaultDeepSeekBaseURL(),
      client: this.#getClient(),
      model,
      metadata: this.getModelMetadata(model),
    });
    this.#modelCache.set(cacheKey, resolvedModel);
    return resolvedModel;
  }

  /**
   * Closes cached model wrappers and clears cache.
   */
  async close(): Promise<void> {
    const cachedModels = Array.from(new Set(this.#modelCache.values()));
    this.#modelCache.clear();

    const closeErrors: unknown[] = [];
    await Promise.all(
      cachedModels.map(async (model) => {
        const maybeClose = (model as { close?: () => Promise<void> | void })
          .close;
        if (typeof maybeClose !== 'function') {
          return;
        }
        try {
          await maybeClose.call(model);
        } catch (error) {
          closeErrors.push(error);
        }
      }),
    );

    if (closeErrors.length === 1) {
      throw closeErrors[0];
    }
    if (closeErrors.length > 1) {
      const error = new Error('Failed to close DeepSeekProvider.');
      (error as Error & { causes?: unknown[] }).causes = closeErrors;
      throw error;
    }
  }
}

function resolveConfiguredModelMetadata(
  resolver: DeepSeekProviderOptions['modelMetadata'],
  modelName: string,
): ModelMetadata | undefined {
  if (!resolver) {
    return undefined;
  }
  if (typeof resolver === 'function') {
    return resolver(modelName);
  }
  return resolver[modelName] ?? resolver[modelName.toLowerCase()];
}

function getKnownDeepSeekModelMetadata(
  modelName: string,
): ModelMetadata | undefined {
  const normalized = modelName.trim().toLowerCase();
  if (
    normalized === 'deepseek-chat' ||
    normalized === 'deepseek-reasoner' ||
    /^deepseek-v4-(?:flash|pro)(?:-\d{4}-\d{2}-\d{2})?$/.test(normalized)
  ) {
    return {
      contextWindowTokens: 1_000_000,
      // contextWindowTokens: 64_000,
      maxOutputTokens: 32_000,
    };
  }
  return undefined;
}
